/**
 * Implied Facts — pure helpers shared by the Resume Editor card and the
 * post-close review modal.
 *
 * Mirrors the backend rules in `shared/implied_facts.py`:
 *   - a fact is `proposed` until the user confirms it; only CONFIRMED facts are
 *     ground truth for generation
 *   - editing or hand-adding a fact makes it `source: 'user'`, which the AI is
 *     never allowed to modify or delete
 *   - near-duplicate detection is a case/punctuation-insensitive compare, the
 *     same normalization the backend merge uses
 *   - trashing an AI fact DISMISSES it (a hidden tombstone) rather than deleting
 *     it: the extraction model never sees prior facts, so a deleted fact is
 *     simply re-derived and re-asked on the next run. The tombstone's topic key
 *     is what suppresses it. A fact the USER authored is genuinely deleted —
 *     they typed it, nothing will re-propose it.
 *   - in the review modal every per-item answer (confirm / trash / edit) is
 *     saved IMMEDIATELY and closing saves nothing, so those writes are
 *     serialized through `createFactSaveQueue` below
 *
 * Everything here is pure so the merge/chip/modal logic is unit-testable
 * without rendering React (the app's vitest setup has no DOM testing library).
 */
import type { ImpliedFact } from '../types';

/** Max characters in a fact — mirrors MAX_FACT_CHARS in shared/implied_facts.py. */
export const MAX_FACT_CHARS = 120;

/** Stable-ish client-side id. The backend uses the same `if_` prefix. */
export function newFactId(): string {
  return `if_${Math.random().toString(36).slice(2, 10)}`;
}

/** Comparison key for near-duplicate detection (mirrors `normalize_fact`). */
export function normalizeFactText(text: string): string {
  return (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9+% ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Trim to MAX_FACT_CHARS at a WORD boundary — never append an ellipsis. */
export function trimFact(text: string): string {
  const t = (text || '').trim();
  if (t.length <= MAX_FACT_CHARS) return t;
  const cut = t.slice(0, MAX_FACT_CHARS);
  const space = cut.lastIndexOf(' ');
  return (space > 40 ? cut.slice(0, space) : cut).replace(/[ ,;:-]+$/, '');
}

export function factsOf(facts: ImpliedFact[] | undefined): ImpliedFact[] {
  return Array.isArray(facts) ? facts : [];
}

/**
 * Everything the user should SEE. Dismissed facts ride along in the stored list
 * as re-proposal suppressors and must never be rendered anywhere.
 */
export function visibleFacts(facts: ImpliedFact[] | undefined): ImpliedFact[] {
  return factsOf(facts).filter(f => f.status !== 'dismissed');
}

export function proposedFacts(facts: ImpliedFact[] | undefined): ImpliedFact[] {
  return factsOf(facts).filter(f => f.status === 'proposed' && !!f.fact);
}

export function confirmedFacts(facts: ImpliedFact[] | undefined): ImpliedFact[] {
  return factsOf(facts).filter(f => f.status === 'confirmed' && !!f.fact);
}

export function hasProposedFacts(facts: ImpliedFact[] | undefined): boolean {
  return proposedFacts(facts).length > 0;
}

/** Confirm one fact. A confirmed fact becomes ground truth for generation. */
export function confirmFact(facts: ImpliedFact[] | undefined, id: string): ImpliedFact[] {
  return factsOf(facts).map(f => (f.id === id ? { ...f, status: 'confirmed' as const } : f));
}

/** Confirm every proposal in one click. */
export function confirmAllFacts(facts: ImpliedFact[] | undefined): ImpliedFact[] {
  return factsOf(facts).map(f =>
    f.status === 'proposed' ? { ...f, status: 'confirmed' as const } : f);
}

export function removeFact(facts: ImpliedFact[] | undefined, id: string): ImpliedFact[] {
  return factsOf(facts).filter(f => f.id !== id);
}

/**
 * The trash / "not right" action, everywhere in the UI.
 *
 * An AI-sourced fact becomes a DISMISSED tombstone: it disappears from every
 * view but stays in the stored list, and the backend merge suppresses any
 * future proposal that shares its topic key. Delete it outright and the very
 * next extraction re-derives it and asks again — that is the bug this exists
 * to close. A USER-authored fact is genuinely removed; nothing re-proposes it.
 */
export function dismissFact(facts: ImpliedFact[] | undefined, id: string): ImpliedFact[] {
  const target = factsOf(facts).find(f => f.id === id);
  if (!target) return factsOf(facts);
  if (target.source === 'user') return removeFact(facts, id);
  return factsOf(facts).map(f =>
    (f.id === id ? { ...f, status: 'dismissed' as const } : f));
}

/**
 * Edit a fact's text. An edited fact is USER-owned and confirmed: the user has
 * read it and rewritten it, which is a stronger signal than a plain confirm,
 * and it must never be silently replaced by the next extraction run.
 * An edit to whitespace-only is ignored (use removeFact to delete).
 */
export function updateFactText(
  facts: ImpliedFact[] | undefined,
  id: string,
  text: string,
): ImpliedFact[] {
  const next = trimFact(text);
  if (!next) return factsOf(facts);
  return factsOf(facts).map(f =>
    f.id === id
      ? { ...f, fact: next, source: 'user' as const, status: 'confirmed' as const }
      : f);
}

/**
 * Add a fact the user typed. Confirmed + user-sourced on arrival; a
 * near-duplicate of a VISIBLE fact is a no-op (returns the list unchanged).
 * Dismissed tombstones are deliberately not consulted — re-typing something you
 * once trashed must work, or the add box silently does nothing.
 */
export function addUserFact(
  facts: ImpliedFact[] | undefined,
  text: string,
  idFactory: () => string = newFactId,
): ImpliedFact[] {
  const list = factsOf(facts);
  const clean = trimFact(text);
  if (!clean) return list;
  const key = normalizeFactText(clean);
  if (!key || visibleFacts(list).some(f => normalizeFactText(f.fact) === key)) return list;
  return [...list, {
    id: idFactory(),
    fact: clean,
    status: 'confirmed',
    source: 'user',
    created_at: new Date().toISOString(),
  }];
}

export interface FactSaveQueue {
  /** Queue this list and get the promise of the drain that writes it. */
  push: (next: ImpliedFact[]) => Promise<boolean>;
  /** A new proposal set arrived — this list is now the server's truth. */
  reseed: (facts: ImpliedFact[]) => void;
  /** The last list the server accepted. */
  readonly lastSaved: ImpliedFact[];
}

/**
 * Serialize the review modal's saves.
 *
 * Every per-item answer (confirm / trash / edit commit) writes IMMEDIATELY — a
 * decision must not depend on the user finding the Done button — so a fast
 * trash-trash-confirm fires three writes at once. This keeps at most ONE save in
 * flight and queues only the NEWEST list behind it (each list is the whole
 * state, so an older one has nothing to contribute): last write wins, always in
 * order, never interleaved.
 *
 * On failure it stops, reports the last list the SERVER accepted so the caller
 * can roll the UI back to it, and lets the user retry. Never throws.
 *
 * Lives here, not in the component, so the race can be tested without rendering
 * React (the app's vitest setup has no DOM testing library).
 */
export function createFactSaveQueue(
  save: (facts: ImpliedFact[]) => Promise<void>,
  hooks: {
    onSavingChange?: (saving: boolean) => void;
    onFailure?: (lastSaved: ImpliedFact[]) => void;
  } = {},
  seed: ImpliedFact[] = [],
): FactSaveQueue {
  let pending: ImpliedFact[] | null = null;
  let inFlight: Promise<boolean> | null = null;
  let lastSaved: ImpliedFact[] = seed;

  async function drain(): Promise<boolean> {
    hooks.onSavingChange?.(true);
    let ok = true;
    while (pending) {
      const attempt = pending;
      pending = null;
      try {
        await save(attempt);
        lastSaved = attempt;
      } catch {
        ok = false;
        break;
      }
    }
    pending = null;
    hooks.onSavingChange?.(false);
    if (!ok) hooks.onFailure?.(lastSaved);
    return ok;
  }

  return {
    push(next: ImpliedFact[]): Promise<boolean> {
      pending = next;
      if (!inFlight) inFlight = drain().finally(() => { inFlight = null; });
      return inFlight;
    },
    reseed(facts: ImpliedFact[]) {
      pending = null;
      lastSaved = facts;
    },
    get lastSaved() { return lastSaved; },
  };
}

/**
 * Decide whether the post-close review modal should open.
 *
 * `runId` is the run the editor's trigger call reported; the parent only acts
 * on the record once that exact run has finished, so a stale marker left by an
 * earlier run can never pop the modal. Reopening the editor cancels it — the
 * section card shows the same proposals in place.
 */
export function shouldOpenReview(args: {
  runId: string | null;
  state: { run_id?: string; status?: string } | undefined;
  facts: ImpliedFact[] | undefined;
  editorOpen: boolean;
}): boolean {
  const { runId, state, facts, editorOpen } = args;
  if (!runId || editorOpen) return false;
  if (!state || state.run_id !== runId || state.status !== 'done') return false;
  return hasProposedFacts(facts);
}
